import {
  AlignCenterIcon,
  Column,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  SelectOptionProps,
  SupportRadioIcon,
  SupportTvIcon,
  TablePaginated,
  TablePaginatedOptions,
  Typography,
} from '@acceso/design-system';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableRowInfo } from '@/components/TableRowInfo/TableRowInfo';
import { TableRowSchedule } from '@/components/TableRowSchedule/TableRowSchedule';
import { UserAction } from '@/components/UserAction';
import { PRIORITY } from '@/features/broadcast-schedule/constants';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { ChannelLogoSlot } from '@/features/broadcasters/components/ChannelLogoSlot/ChannelLogoSlot';
import { useChannelsByIds } from '@/features/broadcasters/hooks/useChannelsByIds';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import {
  DEFAULT_CHANNEL_LOGO,
  parentIdsWithoutOwnLogo,
  resolveChannelLogo,
} from '@/features/broadcasters/utils/channel-logo';
import { useLanguages } from '@/features/languages/api/get-languages';
import { usePagination } from '@/hooks/usePagination';
import { PaginatedResponse } from '@/types/api';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';

import { useProgramFilters } from '../../store/program-filters';
import { TProgram } from '../../types';

interface ParentProgramTableProps {
  items: PaginatedResponse<TProgram> | undefined;
  isFetching?: boolean;
  onUpdate?: (broadcasters: TProgram) => void;
  onDelete?: (broadcasters: TProgram) => void;
  onSelect?: (broadcasters: TProgram) => void;
  onGenerateBroadcasts?: (program: TProgram) => void;
}

export function ProgramTable({
  items,
  isFetching,
  onUpdate,
  onDelete,
  onSelect,
  onGenerateBroadcasts,
}: ParentProgramTableProps) {
  const { t } = useTranslation();
  const { data: languages = [] } = useLanguages();
  const { data: { data: channels = [] } = {} } = useChannels({
    params: { isParentChannel: false },
  });
  // The list above only holds child channels, so the parents a logo-less child
  // falls back to are looked up apart, for the rows on screen only.
  const parentIds = useMemo(
    () => parentIdsWithoutOwnLogo(items?.data.map(item => item.channel) ?? []),
    [items]
  );
  const { channelsById: parentsById, isPending: isParentsPending } = useChannelsByIds(parentIds);
  const { paginationValues, setFilters, setPaginationValues, resetFilters } = useProgramFilters();

  const priorityOptions = useMemo(
    () =>
      PRIORITY.map(priority => ({
        label: priority.value,
        value: priority.value,
      })) as unknown as SelectOptionProps[],
    []
  );

  const channelsOptions = useMemo(() => {
    return [{ value: '', label: 'Emisoras', icon: AlignCenterIcon }].concat(
      channels.map(channel => ({
        label: channel.short_name ? `${channel.name} (${channel.short_name})` : channel.name,
        image: channel.parameters?.logo_presigned_url || DEFAULT_CHANNEL_LOGO,
        value: channel.id,
        icon: channel.media_type === ChannelMediaTypeEnum.RADIO ? SupportRadioIcon : SupportTvIcon,
      }))
    ) as unknown as SelectOptionProps[];
  }, [channels]);

  const handleResetFilters = () => {
    resetFilters();
  };

  const handlePriorityChange = (priority: string | string[]) => {
    if (!Array.isArray(priority)) return;

    const numericPriorities = priority.map(p => parseInt(p));

    setFilters(prev => ({
      ...prev,
      priorities: numericPriorities,
    }));
  };

  const handleChannelChange = (channel: string | string[]) => {
    if (Array.isArray(channel)) return;
    setFilters(prev => {
      return { ...prev, channel_id: channel };
    });
  };

  const handleChangeState = (newState: string | string[]) => {
    if (Array.isArray(newState)) return;
    setFilters(prev => {
      return { ...prev, state: (newState as 'active' | 'inactive' | '') || '' };
    });
  };

  const handleChangeChannelMediaType = (newMediaType: string | string[]) => {
    if (Array.isArray(newMediaType)) return;
    setFilters(prev => {
      return { ...prev, channel_media_type: newMediaType as ChannelMediaTypeEnum };
    });
  };

  const handleChangeStartDate = (value: Date) => {
    setFilters(prev => {
      return { ...prev, gte: value.toISOString() };
    });
  };

  const handleChangeEndDate = (value: Date) => {
    setFilters(prev => {
      return { ...prev, lte: value.toISOString() };
    });
  };

  const pagination = usePagination();

  useEffect(() => {
    if (
      pagination.values.query !== paginationValues?.query ||
      pagination.values.offset !== paginationValues?.offset ||
      pagination.values.currentPage !== paginationValues?.currentPage ||
      pagination.values.rowsPerPage !== paginationValues?.rowsPerPage ||
      !isEqual(pagination.values.sortBy, paginationValues?.sortBy)
    ) {
      setPaginationValues(pagination.values);
    }
  }, [
    pagination.values.query,
    pagination.values.offset,
    pagination.values.currentPage,
    pagination.values.rowsPerPage,
    pagination.values.sortBy,
  ]);

  const options: Partial<TablePaginatedOptions> = useMemo(
    () => ({
      pagination: {
        changePage: pagination.filters.onChangePage,
        changeRows: pagination.filters.onChangeRows,
        currentPage: pagination.values.currentPage,
        itemsPerPage: pagination.values.rowsPerPage,
      },
      setSortBy: pagination.filters.onSortBy,
      sortBy: pagination.values.sortBy,
      query: pagination.values.query,
      setQuery: pagination.filters.onChangeQuery,
      onCleanAllFilters: handleResetFilters,
      filters: [
        {
          name: 'priority',
          placeholder: t('Prioridad'),
          options: priorityOptions,
          isMultiple: true,
          onChange: item => {
            handlePriorityChange(item);
          },
        },
        {
          name: 'support',
          placeholder: t('Soporte'),
          options: [
            {
              label: t('Soportes'),
              value: '',
              icon: AlignCenterIcon,
            },
            {
              label: t('Tv'),
              icon: SupportTvIcon,
              value: 'tv',
            },
            {
              label: t('Radio'),
              icon: SupportRadioIcon,
              value: 'r',
            },
          ],
          onChange: item => {
            handleChangeChannelMediaType(item);
          },
        },
        {
          name: 'channel',
          placeholder: t('Emisora'),
          options: channelsOptions,
          onChange: item => {
            handleChannelChange(item);
          },
        },
        {
          name: 'state',
          placeholder: t('Estado'),
          options: [
            {
              label: t('Estado'),
              value: '',
            },
            {
              label: t('Activo'),
              value: 'active',
            },
            {
              label: t('Inactivo'),
              value: 'inactive',
            },
          ],
          onChange: item => {
            handleChangeState(item);
          },
        },
      ],
      dateFilter: withAppTzDateFilter({
        name: 'period',
        onChange: item => {
          if (!item) {
            setFilters({ gte: '', lte: '' });
          }
          if (item.start) {
            handleChangeStartDate(item.start);
          }

          if (item.end) {
            handleChangeEndDate(item.end);
          }
        },
        placeholder: t('Fecha de creación'),
        showHours: true,
      }),
      searchBarPlaceholder: t('Buscar por programa o descripción'),
    }),
    [
      priorityOptions,
      channelsOptions,
      pagination.values.currentPage,
      pagination.values.rowsPerPage,
      pagination.values.sortBy,
      pagination.values.query,
    ]
  );

  const columns: Column<TProgram>[] = useMemo(
    () => [
      {
        sortable: true,
        width: '20%',
        id: 'name',
        title: t('Programa'),
        valueFn: item => item.name,
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        render: item => {
          return (
            <TableRowInfo
              showTooltip
              title={item.name}
              icon={
                item.channel?.media_type === ChannelMediaTypeEnum.RADIO
                  ? SupportRadioIcon
                  : SupportTvIcon
              }
            />
          );
        },
      },
      {
        id: 'priority',
        className: 'cursor-pointer',
        width: '5%',
        title: t('Prioridad'),
        sortable: true,
        valueFn: item => item.priority,
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return (
            <Typography
              fontSize={FontSizeEnum.Basic}
              weight={FontWeightEnum.Semibold}
              color={FontColorEnum.Dark10}
            >
              {PRIORITY[item.priority - 1].render}
            </Typography>
          );
        },
      },

      {
        width: '20%',
        id: 'description',
        valueFn: item => item.description,
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        sortable: true,
        title: t('Descripción'),
        render: item => {
          return <TableRow content={(item?.description as string) || ''} />;
        },
      },
      {
        width: '15%',
        id: 'channel',
        sortable: true,
        title: t('Emisora'),
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        valueFn: item => item.channel?.short_name,
        render: item => {
          // Each row carries its channel, so it is never pending.
          const logo = resolveChannelLogo(item.channel, {
            parentsById,
            isParentsPending,
            isChannelPending: false,
          });

          return (
            <div className="flex items-center gap-2">
              <ChannelLogoSlot source={logo} alt={item.channel?.short_name || ''} />
              <TableRow content={item.channel?.short_name || ''} />
            </div>
          );
        },
      },
      {
        width: '25%',
        sortable: true,
        id: 'schedules',
        title: t('Programación'),
        valueFn: item => item.start_datetime,
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        render: item => (
          <TableRowSchedule
            startDatetime={item.start_datetime}
            endDatetime={item.end_datetime}
            schedule={item.schedule}
          />
        ),
      },
      {
        width: '5%',
        id: 'language',
        title: t('Idioma'),
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        valueFn: item => item.language_id,
        render: item => {
          const language = languages.find(language => language.id === item.language_id);
          return <TableRow content={(language?.name as string) || ''} />;
        },
      },
      {
        width: '5%',
        sortable: true,
        onColumnClick: item => onSelect?.(item),
        className: 'cursor-pointer',
        id: 'state',
        title: t('Estado'),
        valueFn: item => item.state,
        render: item => {
          return <StateActiveInTable status={item.state} />;
        },
      },
      {
        id: 'updated_by',
        width: '15%',
        title: t('Actualizado por'),
        sortable: true,
        valueFn: item => item.updated_at ?? '',
        onColumnClick: item => onSelect?.(item),
        render: item => {
          if (!item.updated_at) return null;
          return <UserAction id={item.updated_by ?? undefined} timestamp={item.updated_at} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDelete={() => onDelete?.(item)}
            onAddScheduleEmisionIcon={
              onGenerateBroadcasts ? () => onGenerateBroadcasts(item) : undefined
            }
            tooltips={{ addScheduleEmisionIcon: t('Generar emisiones') }}
          />
        ),
      },
    ],
    [languages, parentsById, isParentsPending, onUpdate, onDelete, onGenerateBroadcasts]
  );

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items?.data || []}
      totalItems={items?.total || 0}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
